import { useState, useEffect } from 'react';
import {
  Title,
  Group,
  Button,
  Stack,
  Paper,
  Text,
  Table,
  Badge,
  Drawer,
  Tabs,
  Card,
  Timeline,
  Grid,
  ActionIcon,
  Menu,
  Pagination,
  Skeleton,
  Alert,
  Code,
  List,
  Progress,
  Box,
  Modal,
  Select,
  TextInput,

} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconListCheck,
  IconRefresh,
  IconDotsVertical,
  IconClock,
  IconCheck,
  IconX,
  IconDeviceDesktop,
  IconInfoCircle,
  IconHistory,
  IconPlus,
  IconTerminal,
  IconAlertCircle,
} from '@tabler/icons-react';

import { useTasks, useTask, useCancelTask, useRetryTask, useRelayAgents, useCreateRelayTask, useDevices } from '@/hooks';
import { StatusBadge } from '@/components/common/StatusBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
// TaskStatus type is used in the TaskDrawerContent for type inference

export function TasksPage() {
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure();
  const [createModalOpened, { open: openCreateModal, close: closeCreateModal }] = useDisclosure();
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [visibleRows, setVisibleRows] = useState<Set<number>>(new Set());
  const [pageVisible, setPageVisible] = useState(false);

  // Page enter animation
  useEffect(() => {
    const timer = setTimeout(() => setPageVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  // Fetch tasks from API
  const { data, isLoading, isError, refetch } = useTasks({
    page,
    status: statusFilter || undefined,
  });

  // Fetch relay agents for task creation
  const { data: agents } = useRelayAgents();
  const { data: devicesData } = useDevices({ page: 1, limit: 100 });
  const devices = devicesData?.devices || [];

  // Task mutations
  const cancelTaskMutation = useCancelTask();
  const retryTaskMutation = useRetryTask();
  const createTaskMutation = useCreateRelayTask();

  // Form for creating tasks
  const createTaskForm = useForm({
    initialValues: {
      type: 'ping',
      agentId: '',
      targetIp: '',
      hostname: '',
    },
    validate: {
      agentId: (value: string) => (!value ? 'Please select an agent' : null),
      targetIp: (value: string, values: { type: string }) => {
        if (values.type === 'ping' || values.type === 'wol') {
          if (!value) return 'Target IP/MAC is required';
        }
        return null;
      },
    },
  });

  const handleCreateTask = async (values: typeof createTaskForm.values) => {
    try {
      const payload: Record<string, unknown> = {};

      if (values.type === 'ping') {
        payload.device_ip = values.targetIp;
        payload.hostname = values.hostname || values.targetIp;
      } else if (values.type === 'wol') {
        payload.mac_address = values.targetIp;
        payload.hostname = values.hostname || 'WOL Target';
      }

      await createTaskMutation.mutateAsync({
        type: values.type,
        agent_id: values.agentId,
        payload,
      });

      notifications.show({
        title: 'Task created',
        message: `${values.type} task has been created successfully`,
        color: 'green',
      });

      closeCreateModal();
      createTaskForm.reset();
      refetch();
    } catch (error) {
      notifications.show({
        title: 'Failed to create task',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const tasks = data?.tasks || [];
  const totalPages = data?.totalPages || 1;

  // Animate rows when tasks change
  useEffect(() => {
    setVisibleRows(new Set());
    tasks.forEach((_, index) => {
      setTimeout(() => {
        setVisibleRows((prev) => new Set([...prev, index]));
      }, index * 60);
    });
  }, [tasks]);

  const handleRowClick = (taskId: string) => {
    setSelectedTask(taskId);
    openDrawer();
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      await cancelTaskMutation.mutateAsync(taskId);
      notifications.show({
        title: 'Task cancelled',
        message: 'The task has been cancelled successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to cancel task',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  const handleRetryTask = async (taskId: string) => {
    try {
      await retryTaskMutation.mutateAsync(taskId);
      notifications.show({
        title: 'Task retried',
        message: 'The task has been resubmitted',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Failed to retry task',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    }
  };

  // Helper to format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box
      style={{
        opacity: pageVisible ? 1 : 0,
        transform: pageVisible ? 'translateY(0)' : 'translateY(10px)',
        transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <Stack gap="lg">
        {/* Page header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Tasks</Title>
            <Text c="dimmed" size="sm">
              Monitor task execution and verification status
            </Text>
          </div>

          <Group>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={openCreateModal}
              style={{
                transition: 'all 200ms ease',
              }}
            >
              Create Task
            </Button>
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} className={isLoading ? 'icon-spin' : ''} />}
              onClick={() => refetch()}
              loading={isLoading}
              style={{
                transition: 'all 200ms ease',
              }}
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {/* Quick filters */}
        <Group>
          <Badge
            variant={statusFilter === '' ? 'filled' : 'light'}
            style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
            className="btn-press"
            onClick={() => setStatusFilter('')}
          >
            All ({tasks.length})
          </Badge>
          <Badge
            color="yellow"
            variant={statusFilter === 'executing' ? 'filled' : 'light'}
            style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
            className="btn-press"
            onClick={() => setStatusFilter('executing')}
          >
            Running ({tasks.filter((t) => t.status === 'executing').length})
          </Badge>
          <Badge
            color="blue"
            variant={statusFilter === 'scheduled' ? 'filled' : 'light'}
            style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
            className="btn-press"
            onClick={() => setStatusFilter('scheduled')}
          >
            Scheduled ({tasks.filter((t) => t.status === 'scheduled').length})
          </Badge>
          <Badge
            color="red"
            variant={statusFilter === 'failed' ? 'filled' : 'light'}
            style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
            className="btn-press"
            onClick={() => setStatusFilter('failed')}
          >
            Failed ({tasks.filter((t) => t.status === 'failed').length})
          </Badge>
          <Badge
            color="green"
            variant={statusFilter === 'completed' ? 'filled' : 'light'}
            style={{ cursor: 'pointer', transition: 'all 200ms ease' }}
            className="btn-press"
            onClick={() => setStatusFilter('completed')}
          >
            Completed ({tasks.filter((t) => t.status === 'completed').length})
          </Badge>
        </Group>

        {/* Tasks table */}
        <Paper withBorder style={{ transition: 'all 300ms ease' }}>
          {isLoading ? (
            <Stack p="md" gap="sm">
              {[...Array(5)].map((_, i) => (
                <Skeleton
                  key={i}
                  height={50}
                  animate
                  className="skeleton-shimmer"
                  style={{
                    animationDelay: `${i * 100}ms`,
                  }}
                />
              ))}
            </Stack>
          ) : isError ? (
            <ErrorState
              message="Failed to load tasks"
              onRetry={() => refetch()}
            />
          ) : tasks.length === 0 ? (
            <EmptyState
              icon={<IconListCheck size={48} />}
              title="No tasks found"
              description="No tasks match the current filter"
            />
          ) : (
            <>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Host</Table.Th>
                    <Table.Th>Image</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th w={50}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tasks.map((task, index) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const fogTask = task as any;
                    return (
                      <Table.Tr
                        key={task.id}
                        style={{
                          cursor: 'pointer',
                          opacity: visibleRows.has(index) ? 1 : 0,
                          transform: visibleRows.has(index) ? 'translateX(0)' : 'translateX(-10px)',
                          transition: 'all 300ms cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                        className="table-row-hover"
                        onClick={() => handleRowClick(task.id)}
                      >
                        <Table.Td>
                          <Group gap="xs">
                            <IconDeviceDesktop size={14} />
                            <Text size="sm" fw={500}>{fogTask.hostname || 'Unknown'}</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{fogTask.imageName || '-'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge variant="light" size="sm">
                            {task.type}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge type="taskStatus" value={task.status} />
                        </Table.Td>
                        <Table.Td>
                          {fogTask.progress !== undefined && fogTask.progress !== null ? (
                            <Group gap="xs">
                              <Progress
                                value={fogTask.progress}
                                size="sm"
                                w={60}
                                animated={fogTask.isActive}
                                className="progress-animated"
                              />
                              <Text size="xs" c="dimmed">{fogTask.progress}%</Text>
                            </Group>
                          ) : (
                            <Text size="sm" c="dimmed">-</Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{formatDate(task.createdAt)}</Text>
                        </Table.Td>
                        <Table.Td onClick={(e) => e.stopPropagation()}>
                          <Menu position="bottom-end">
                            <Menu.Target>
                              <ActionIcon variant="subtle" size="sm">
                                <IconDotsVertical size={16} />
                              </ActionIcon>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item
                                leftSection={<IconInfoCircle size={14} />}
                                onClick={() => handleRowClick(task.id)}
                              >
                                View details
                              </Menu.Item>
                              {task.status === 'failed' && (
                                <Menu.Item
                                  leftSection={<IconRefresh size={14} />}
                                  onClick={() => handleRetryTask(task.id)}
                                >
                                  Retry
                                </Menu.Item>
                              )}
                              {(task.status === 'queued' ||
                                task.status === 'scheduled') && (
                                <Menu.Item
                                  leftSection={<IconX size={14} />}
                                  color="red"
                                  onClick={() => handleCancelTask(task.id)}
                                >
                                  Cancel
                                </Menu.Item>
                              )}
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>

              <Group justify="center" p="md">
                <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />
              </Group>
            </>
          )}
        </Paper>
      </Stack>

      {/* Task detail drawer */}
      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={
          <Group>
            <IconListCheck size={20} />
            <Text fw={600}>Task Details</Text>
          </Group>
        }
        size="lg"
        transitionProps={{
          transition: 'slide-left',
          duration: 300,
        }}
      >
        {selectedTask && <TaskDrawerContent taskId={selectedTask} />}
      </Drawer>

      {/* Create Task modal */}
      <Modal
        opened={createModalOpened}
        onClose={closeCreateModal}
        title={
          <Group>
            <IconPlus size={20} />
            <Text fw={600}>Create Relay Task</Text>
          </Group>
        }
        size="md"
      >
        <form onSubmit={createTaskForm.onSubmit(handleCreateTask)}>
          <Stack gap="md">
            <Select
              label="Task Type"
              placeholder="Select task type"
              data={[
                { value: 'ping', label: 'Ping - Test device reachability' },
                { value: 'wol', label: 'Wake-on-LAN - Wake up device' },
              ]}
              {...createTaskForm.getInputProps('type')}
            />

            <Select
              label="Relay Agent"
              placeholder="Select agent to execute task"
              data={
                agents?.map((agent) => ({
                  value: agent.id,
                  label: `${agent.name} (${agent.status})`,
                })) || []
              }
              {...createTaskForm.getInputProps('agentId')}
              error={createTaskForm.errors.agentId}
            />

            {createTaskForm.values.type === 'wol' ? (
              <TextInput
                label="MAC Address"
                placeholder="AA:BB:CC:DD:EE:FF"
                description="MAC address of the device to wake"
                {...createTaskForm.getInputProps('targetIp')}
              />
            ) : (
              <Select
                label="Target IP"
                placeholder="Select device or type IP"
                description={`${devices.length} devices available`}
                data={devices.map((d) => ({
                  value: d.ipAddress || d.id,
                  label: `${d.ipAddress || 'No IP'} - ${d.hostname}`,
                }))}
                value={createTaskForm.values.targetIp || null}
                onChange={(value) => createTaskForm.setFieldValue('targetIp', value || '')}
                searchable
                clearable
                nothingFoundMessage="No devices found"
                error={createTaskForm.errors.targetIp}
              />
            )}

            <TextInput
              label="Hostname (optional)"
              placeholder="Device name for display"
              description="Friendly name for this device"
              {...createTaskForm.getInputProps('hostname')}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeCreateModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createTaskMutation.isPending}
                leftSection={<IconPlus size={16} />}
              >
                Create Task
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
}

// Task drawer content component
function TaskDrawerContent({ taskId }: { taskId: string }) {
  const { data: task, isLoading, isError } = useTask(taskId);

  // Helper to format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  if (isLoading) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={200} />
        <Skeleton height={100} />
      </Stack>
    );
  }

  if (isError || !task) {
    return (
      <Alert color="red" icon={<IconX size={16} />}>
        Failed to load task details
      </Alert>
    );
  }

  // Check if this is a relay task (has result with data)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const taskData = task as any;
  const isRelayTask = ['scan_script', 'ping', 'wol'].includes(task.type?.toLowerCase() || '');
  const hasResult = taskData.result && typeof taskData.result === 'object';

  const events = task.events || [];
  const verificationResults = task.verificationResults || [];
  const failedCount = verificationResults.filter((r) => r.outcome === 'failed').length;

  return (
    <Tabs defaultValue={isRelayTask && hasResult ? 'results' : 'overview'}>
      <Tabs.List>
        <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
          Overview
        </Tabs.Tab>
        {isRelayTask && (
          <Tabs.Tab value="results" leftSection={<IconTerminal size={14} />}>
            Results
          </Tabs.Tab>
        )}
        <Tabs.Tab value="timeline" leftSection={<IconHistory size={14} />}>
          Timeline
        </Tabs.Tab>
        {!isRelayTask && (
          <>
            <Tabs.Tab value="verification" leftSection={<IconCheck size={14} />}>
              Verification
            </Tabs.Tab>
            <Tabs.Tab value="devices" leftSection={<IconDeviceDesktop size={14} />}>
              Devices
            </Tabs.Tab>
          </>
        )}
      </Tabs.List>

      <Tabs.Panel value="overview" pt="md">
        <Stack gap="md">
          <Card withBorder>
            <Grid>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Task ID
                </Text>
                <Code>{task.id.substring(0, 8)}</Code>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Type
                </Text>
                <Badge variant="light">{task.type}</Badge>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Status
                </Text>
                <StatusBadge type="taskStatus" value={task.status} />
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Devices
                </Text>
                <Text fw={500}>{task.deviceCount}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Created By
                </Text>
                <Text size="sm">{task.createdBy}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Approved By
                </Text>
                <Text size="sm">{task.approvedBy || '-'}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Created At
                </Text>
                <Text size="sm">{formatDate(task.createdAt)}</Text>
              </Grid.Col>
              <Grid.Col span={6}>
                <Text size="sm" c="dimmed">
                  Completed At
                </Text>
                <Text size="sm">{formatDate(task.completedAt)}</Text>
              </Grid.Col>
            </Grid>
          </Card>

          {task.status === 'failed' && failedCount > 0 && (
            <Alert color="red" icon={<IconX size={16} />} title="Task Failed">
              {failedCount} of {task.deviceCount} devices failed verification. Check the Verification tab for
              details.
            </Alert>
          )}

          {/* Show error for relay tasks */}
          {isRelayTask && task.status === 'failed' && taskData.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Task Error">
              {taskData.error}
            </Alert>
          )}
        </Stack>
      </Tabs.Panel>

      {/* Results panel for relay tasks */}
      {isRelayTask && (
        <Tabs.Panel value="results" pt="md">
          <Stack gap="md">
            {!hasResult ? (
              <Text c="dimmed" size="sm">No results available yet</Text>
            ) : (
              <>
                {/* Success indicator - check exit_code for script tasks */}
                {(() => {
                  // Determine success: check exit_code first, then success flag
                  const exitCode = taskData.result.data?.exit_code ?? taskData.result.exit_code;
                  const isSuccess = exitCode !== undefined ? exitCode === 0 : taskData.result.success;

                  return (
                    <Alert
                      color={isSuccess ? 'green' : 'red'}
                      icon={isSuccess ? <IconCheck size={16} /> : <IconX size={16} />}
                      title={isSuccess ? 'Script executed successfully' : 'Script execution failed'}
                    >
                      {exitCode !== undefined && (
                        <Text size="sm">Exit code: {exitCode}</Text>
                      )}
                    </Alert>
                  );
                })()}

                {/* Standard output - check both result.data.stdout and result.stdout */}
                {(() => {
                  const stdout = taskData.result.data?.stdout || taskData.result.stdout;
                  if (!stdout) return null;

                  return (
                    <Card withBorder>
                      <Text size="sm" fw={500} mb="xs">Output</Text>
                      {(() => {
                        // Try to parse as JSON for pretty display
                        try {
                          const parsed = JSON.parse(stdout);
                          return (
                            <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
                              {JSON.stringify(parsed, null, 2)}
                            </Code>
                          );
                        } catch {
                          return (
                            <Code block style={{ whiteSpace: 'pre-wrap', maxHeight: 400, overflow: 'auto' }}>
                              {stdout}
                            </Code>
                          );
                        }
                      })()}
                    </Card>
                  );
                })()}

                {/* Standard error - check both result.data.stderr and result.stderr */}
                {(() => {
                  const stderr = taskData.result.data?.stderr || taskData.result.stderr;
                  if (!stderr || stderr.includes('CLIXML')) return null;

                  return (
                    <Card withBorder>
                      <Text size="sm" fw={500} mb="xs" c="red">Errors</Text>
                      <Code block color="red" style={{ whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
                        {stderr}
                      </Code>
                    </Card>
                  );
                })()}

                {/* For ping results */}
                {taskData.result.data?.reachable !== undefined && (
                  <Card withBorder>
                    <Grid>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">Reachable</Text>
                        <Badge color={taskData.result.data.reachable ? 'green' : 'red'}>
                          {taskData.result.data.reachable ? 'Yes' : 'No'}
                        </Badge>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Text size="sm" c="dimmed">ICMP Ping</Text>
                        <Badge color={taskData.result.data.icmp_reachable ? 'green' : 'red'}>
                          {taskData.result.data.icmp_reachable ? 'Yes' : 'No'}
                        </Badge>
                      </Grid.Col>
                      {taskData.result.data.ip && (
                        <Grid.Col span={6}>
                          <Text size="sm" c="dimmed">IP Address</Text>
                          <Text fw={500}>{taskData.result.data.ip}</Text>
                        </Grid.Col>
                      )}
                      {taskData.result.data.ports && Object.keys(taskData.result.data.ports).length > 0 && (
                        <Grid.Col span={12}>
                          <Text size="sm" c="dimmed" mb="xs">Port Status</Text>
                          <Group gap="xs">
                            {Object.entries(taskData.result.data.ports).map(([port, open]) => (
                              <Badge key={port} color={open ? 'green' : 'red'} variant="light">
                                Port {port}: {open ? 'Open' : 'Closed'}
                              </Badge>
                            ))}
                          </Group>
                        </Grid.Col>
                      )}
                    </Grid>
                  </Card>
                )}
              </>
            )}
          </Stack>
        </Tabs.Panel>
      )}

      <Tabs.Panel value="timeline" pt="md">
        {events.length === 0 ? (
          <Text c="dimmed" size="sm">No events recorded</Text>
        ) : (
          <Timeline active={events.length - 1} bulletSize={24} lineWidth={2}>
            {events.map((event) => (
              <Timeline.Item
                key={event.id}
                bullet={
                  event.event.toLowerCase().includes('failed') ? (
                    <IconX size={12} />
                  ) : event.event.toLowerCase().includes('completed') ||
                    event.event.toLowerCase().includes('approved') ? (
                    <IconCheck size={12} />
                  ) : (
                    <IconClock size={12} />
                  )
                }
                title={event.event}
                color={event.event.toLowerCase().includes('failed') ? 'red' : undefined}
              >
                <Text size="xs" c="dimmed">
                  {formatDate(event.timestamp)} • {event.user}
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="verification" pt="md">
        <Stack gap="md">
          {verificationResults.length === 0 ? (
            <Text c="dimmed" size="sm">No verification results available</Text>
          ) : (
            verificationResults.map((result) => (
              <Card key={result.deviceId} withBorder>
                <Group justify="space-between" mb="sm">
                  <Group gap="xs">
                    <IconDeviceDesktop size={16} />
                    <Text fw={500}>{result.hostname}</Text>
                  </Group>
                  <Badge
                    color={result.outcome === 'passed' ? 'green' : result.outcome === 'failed' ? 'red' : 'gray'}
                    variant="light"
                  >
                    {result.outcome}
                  </Badge>
                </Group>

                <Stack gap="xs">
                  {result.checks.map((check, i) => (
                    <Group key={i} justify="space-between">
                      <Group gap="xs">
                        {check.status === 'passed' ? (
                          <IconCheck size={14} color="green" />
                        ) : check.status === 'failed' ? (
                          <IconX size={14} color="red" />
                        ) : (
                          <IconClock size={14} color="gray" />
                        )}
                        <Text size="sm">{check.name}</Text>
                      </Group>
                      <Group gap="xs">
                        {check.status === 'failed' && (
                          <>
                            <Code color="red" fz="xs">
                              {check.actual}
                            </Code>
                            <Text size="xs" c="dimmed">
                              (expected: {check.expected})
                            </Text>
                          </>
                        )}
                      </Group>
                    </Group>
                  ))}
                </Stack>

                {result.score !== undefined && (
                  <Group mt="sm" gap="xs">
                    <Text size="xs" c="dimmed">Verification score:</Text>
                    <Progress value={result.score} size="sm" w={60} color={result.score >= 80 ? 'green' : result.score >= 50 ? 'yellow' : 'red'} />
                    <Text size="xs" c="dimmed">{result.score}%</Text>
                  </Group>
                )}
              </Card>
            ))
          )}
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="devices" pt="md">
        <Text size="sm" c="dimmed" mb="md">
          {task.deviceCount} devices affected by this task
        </Text>
        {task.deviceIds && task.deviceIds.length > 0 ? (
          <List spacing="xs" size="sm">
            {task.deviceIds.map((deviceId) => {
              const verificationResult = verificationResults.find((v) => v.deviceId === deviceId);
              return (
                <List.Item key={deviceId}>
                  <Code fz="xs">{deviceId.substring(0, 8)}</Code>
                  {verificationResult && (
                    <Badge
                      ml="xs"
                      size="xs"
                      color={verificationResult.outcome === 'passed' ? 'green' : verificationResult.outcome === 'failed' ? 'red' : 'gray'}
                      variant="light"
                    >
                      {verificationResult.outcome}
                    </Badge>
                  )}
                </List.Item>
              );
            })}
          </List>
        ) : (
          <Text c="dimmed" size="sm">No device list available</Text>
        )}
      </Tabs.Panel>
    </Tabs>
  );
}
